import { _notNil } from '@/littledash';
import { Handle, type Node, type NodeProps, Position } from '@xyflow/react';
import type { FC } from 'react';
import type { GraphNodeTone, ReadOnlyNodeData } from './Nodes.utils';
import styles from './Nodes.module.scss';

/**
 * Per-tone style classes: `icon` colours the badge, `card` outlines the node so it echoes the status
 * (e.g. red on error). Tone carries the semantic; the engine stays domain-agnostic.
 */
const toneStyles: Record<GraphNodeTone, { icon: string; card: string }> = {
  success: { icon: styles.toneSuccess, card: styles.cardSuccess },
  error: { icon: styles.toneError, card: styles.cardError },
  neutral: { icon: styles.toneNeutral, card: '' },
};

/**
 * A generic, non-interactive card node for GraphCanvas. Renders an optional eyebrow `label`, a `title`,
 * and an optional corner `badge` (icon + tone + tooltip) — all read from `node.data`. Consumers stamp
 * that data during mapping, so this component holds no domain knowledge.
 *
 * Exposes both a top target and bottom source handle so it can sit anywhere in a tree/DAG; the handles
 * are visually hidden (the graph is read-only) but still anchor edges.
 */
const ReadOnlyNode: FC<NodeProps<Node<ReadOnlyNodeData>>> = ({ data }) => {
  const { label, title, badge } = data;

  const tone = _notNil(badge) ? toneStyles[badge.tone ?? 'neutral'] : undefined;
  const cardClass = _notNil(tone) ? `${styles.card} ${tone.card}` : styles.card;

  return (
    <div className={cardClass} data-test-component="ReadOnlyNode">
      <Handle type="target" position={Position.Top} isConnectable={false} className={styles.handle} />
      {_notNil(badge) && (
        <badge.icon
          className={`${styles.badge} ${tone?.icon ?? ''} ${badge.spin === true ? styles.spin : ''}`}
          title={badge.tooltip}
          data-test-element="badge"
        />
      )}
      {_notNil(label) && <span className={styles.label}>{label}</span>}
      <span className={styles.title}>{title}</span>
      <Handle type="source" position={Position.Bottom} isConnectable={false} className={styles.handle} />
    </div>
  );
};

export default ReadOnlyNode;
